import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { AdminPage } from "@/components/admin/AdminSidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";
import { StatsChartCard, type StatsFilters } from "@/components/admin/StatsCharts";
import { StatsFilterBar, rangeForPreset } from "@/components/admin/StatsFilterBar";
import { StatsAgenciasTable } from "@/components/admin/StatsAgenciasTable";
import { StatsRawData } from "@/components/admin/StatsRawData";

const LABELS: Record<string, string> = {
  usuarios_total: "Usuarios registrados",
  usuarios_verificados_total: "Usuarios verificados",
  usuarios_nuevos: "Usuarios nuevos",
  anuncios_publicados_total: "Anuncios publicados",
  anuncios_nuevos: "Anuncios nuevos",
  impresiones_dia: "Impresiones",
  impresiones_unicas_dia: "Impresiones únicas",
  vistas_dia: "Vistas (clicks)",
  vistas_unicas_dia: "Vistas únicas",
  demandas_nuevas: "Demandas nuevas",
  solicitudes_nuevas: "Solicitudes nuevas",
  agencias_activas_total: "Agencias activas",
  agencias_activas_con_usuarios_total: "Agencias activas con usuarios",
};

export const Route = createFileRoute("/admin/stats")({
  head: () => ({
    meta: [{ title: "Estadísticas — Admin" }, { name: "robots", content: "noindex,nofollow" }],
  }),
  component: StatsPage,
});

const METRICS = [
  "usuarios_total",
  "anuncios_total",
  "contactos_total",
  "agencias_total",
  "usuarios_verificados_total",
  "anuncios_publicados_total",
  "anuncios_publicados_verificados_total",
  "anuncios_publicados_agencias_activas",
  "agencias_activas_total",
  "agencias_activas_con_usuarios_total",
  "demandas_activas_total",
  "solicitudes_total",
  "impresiones_total",
  "impresiones_unicas_total",
  "vistas_total",
  "vistas_unicas_total",
  "favoritos_total",
] as const;

type SeriesRow = {
  bucket: string;
  metric: string;
  kind: string;
  value: number;
  value_acum: number;
};

function isoDate(d: Date) {
  return d.toISOString().slice(0, 10);
}

function useStaffGuard() {
  const navigate = useNavigate();
  const [allowed, setAllowed] = useState<boolean | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      const { data: sess } = await supabase.auth.getSession();
      const uid = sess.session?.user.id;
      if (!uid) {
        if (active) setAllowed(false);
        return;
      }
      const { data, error } = await supabase.rpc("is_staff", { _user_id: uid });
      if (!active) return;
      setAllowed(!error && data === true);
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (allowed === false) navigate({ to: "/admin", replace: true });
  }, [allowed, navigate]);

  return allowed;
}

function StatsPage() {
  const allowed = useStaffGuard();

  const [filters, setFilters] = useState<StatsFilters>(() => ({
    ...rangeForPreset("30d"),
    granularity: "day",
    mode: "periodo",
  }));

  const range = useMemo(() => {
    const to = new Date();
    const from = new Date(to.getTime() - 30 * 24 * 60 * 60 * 1000);
    return { from: isoDate(from), to: isoDate(to) };
  }, []);

  const q = useQuery({
    queryKey: ["admin-stats-series", range.from, range.to],
    enabled: allowed === true,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const [series, defs] = await Promise.all([
        supabase.rpc("stats_series", {
          p_metrics: METRICS as unknown as string[],
          p_from: range.from,
          p_to: range.to,
          p_granularity: "day",
        }),
        supabase.from("stats_metric_defs").select("metric, kind, label_es"),
      ]);
      if (series.error) throw series.error;
      if (defs.error) throw defs.error;
      return {
        rows: (series.data ?? []) as SeriesRow[],
        defs: (defs.data ?? []) as Array<{ metric: string; kind: string; label_es: string }>,
      };
    },
  });

  const cards = useMemo(() => {
    const rows = q.data?.rows ?? [];
    const labels = new Map((q.data?.defs ?? []).map((d) => [d.metric, d.label_es]));

    return METRICS.map((metric) => {
      const serie = rows
        .filter((r) => r.metric === metric)
        .sort((a, b) => (a.bucket < b.bucket ? -1 : a.bucket > b.bucket ? 1 : 0));
      const last = serie.at(-1) ?? null;
      const kind = last?.kind ?? "stock";
      const readValue = (r: SeriesRow) => (kind === "flow" ? Number(r.value_acum ?? 0) : Number(r.value ?? 0));

      const current = last ? readValue(last) : null;

      // Valor de hace 7 días (bucket más cercano por debajo del corte)
      let prev: number | null = null;
      if (last) {
        const cutoff = new Date(new Date(last.bucket).getTime() - 7 * 24 * 60 * 60 * 1000)
          .toISOString()
          .slice(0, 10);
        const before = serie.filter((r) => r.bucket.slice(0, 10) <= cutoff).at(-1);
        if (before) prev = readValue(before);
      }

      const delta = current != null && prev != null ? current - prev : null;
      const pct = delta != null && prev ? (delta / Math.abs(prev)) * 100 : null;

      return {
        metric,
        label: labels.get(metric) ?? metric,
        value: current,
        delta,
        pct,
      };
    });
  }, [q.data]);

  if (allowed !== true) {
    return (
      <AdminPage title="Estadísticas" description="Métricas globales de Bizaclia.">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {METRICS.map((m) => (
            <StatSkeleton key={m} />
          ))}
        </div>
      </AdminPage>
    );
  }

  return (
    <AdminPage title="Estadísticas" description="Métricas globales de Bizaclia.">
      {q.error ? (
        <p className="text-sm text-destructive">
          No se pudieron cargar las estadísticas: {(q.error as Error).message}
        </p>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {q.isLoading
          ? METRICS.map((m) => <StatSkeleton key={m} />)
          : cards.map((c) => <StatCard key={c.metric} {...c} />)}
      </div>

      <StatsFilterBar filters={filters} onChange={setFilters} />

      <div className="grid gap-4 lg:grid-cols-2">
        <StatsChartCard
          title="Usuarios registrados y verificados"
          kind="area"
          metrics={["usuarios_total", "usuarios_verificados_total"]}
          labels={LABELS}
          filters={filters}
          enabled={allowed === true}
        />
        <StatsChartCard
          title="Usuarios nuevos"
          kind="bar"
          metrics={["usuarios_nuevos"]}
          labels={LABELS}
          filters={filters}
          enabled={allowed === true}
        />
        <StatsChartCard
          title="Anuncios publicados y nuevos"
          kind="area"
          metrics={["anuncios_publicados_total", "anuncios_nuevos"]}
          labels={LABELS}
          filters={filters}
          enabled={allowed === true}
        />
        <StatsChartCard
          title="Impresiones (totales y únicas)"
          kind="line"
          metrics={["impresiones_dia", "impresiones_unicas_dia"]}
          labels={LABELS}
          filters={filters}
          enabled={allowed === true}
        />
        <StatsChartCard
          title="Vistas (totales y únicas)"
          kind="line"
          metrics={["vistas_dia", "vistas_unicas_dia"]}
          labels={LABELS}
          filters={filters}
          enabled={allowed === true}
        />
        <StatsChartCard
          title="Demandas y solicitudes nuevas"
          kind="bar"
          metrics={["demandas_nuevas", "solicitudes_nuevas"]}
          labels={LABELS}
          filters={filters}
          enabled={allowed === true}
        />
        <StatsChartCard
          title="Agencias activas"
          kind="line"
          metrics={["agencias_activas_total", "agencias_activas_con_usuarios_total"]}
          labels={LABELS}
          filters={filters}
          enabled={allowed === true}
        />
      </div>

      <StatsAgenciasTable enabled={allowed === true} />

      <StatsRawData filters={filters} enabled={allowed === true} />
    </AdminPage>
  );
}


function StatSkeleton() {
  return (
    <Card>
      <CardHeader className="pb-2">
        <Skeleton className="h-4 w-2/3" />
      </CardHeader>
      <CardContent className="space-y-2">
        <Skeleton className="h-8 w-1/2" />
        <Skeleton className="h-3 w-24" />
      </CardContent>
    </Card>
  );
}

const nf = new Intl.NumberFormat("es-ES");

function StatCard({
  label,
  value,
  delta,
  pct,
}: {
  label: string;
  value: number | null;
  delta: number | null;
  pct: number | null;
}) {
  const up = (delta ?? 0) > 0;
  const down = (delta ?? 0) < 0;
  const Icon = up ? ArrowUpRight : down ? ArrowDownRight : Minus;
  const tone = up ? "text-emerald-600" : down ? "text-destructive" : "text-muted-foreground";

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-1">
        <div className="text-2xl font-semibold tabular-nums">
          {value != null ? nf.format(value) : "—"}
        </div>
        <div className={`flex items-center gap-1 text-xs ${tone}`}>
          <Icon className="h-3.5 w-3.5" />
          {delta != null ? (
            <span>
              {delta > 0 ? "+" : ""}
              {nf.format(delta)}
              {pct != null ? ` (${pct > 0 ? "+" : ""}${pct.toFixed(1)}%)` : ""} vs 7 días
            </span>
          ) : (
            <span>Sin datos previos</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
